import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { workspaceAPI, spaceAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { cn, getInitials, formatDate } from '../lib/utils';
import {
    Users,
    Plus,
    UserPlus,
    Trash2,
    Crown,
    Shield,
    User,
    Eye,
    Loader2,
    Mail,
    Clock,
    X,
    Briefcase,
    Edit2,
    ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';

export default function TeamPage() {
    const { currentWorkspace } = useWorkspace();
    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editMemberModal, setEditMemberModal] = useState({ isOpen: false, member: null });
    const [timeout, setTimeoutDelay] = useState(0);

    useEffect(() => {
        if (currentWorkspace) {
            fetchMembers();
            fetchInvitations();
        }
    }, [currentWorkspace]);

    const fetchMembers = async () => {
        setLoading(true);
        try {
            const response = await workspaceAPI.getMembers(currentWorkspace.id);
            setMembers(response.data);
        } catch (error) {
            console.error('Failed to fetch members:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchInvitations = async () => {
        try {
            const response = await workspaceAPI.getInvitations(currentWorkspace.id);
            setInvitations(response.data);
        } catch (error) {
            console.error('Failed to fetch invitations:', error);
        }
    };

    const handleRemoveMember = async (userId, userName) => {
        if (!window.confirm(`Hapus ${userName} dari workspace ini?`)) return;
        try {
            await workspaceAPI.removeMember(currentWorkspace.id, userId);
            toast.success('Member dihapus');
            fetchMembers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal menghapus member');
        }
    };

    const handleCancelInvitation = async (invitationId, email) => {
        if (!window.confirm(`Batalkan undangan untuk ${email}?`)) return;
        try {
            await workspaceAPI.cancelInvitation(currentWorkspace.id, invitationId);
            toast.success('Undangan dibatalkan');
            fetchInvitations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal membatalkan undangan');
        }
    };


    // implementasi countdown timeout
    useEffect(() => {
        if (timeout > 0) {
            const timer = setTimeout(() => {
                setTimeoutDelay(timeout - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [timeout]);


    const handleResendInvitation = async (invitationId, email) => {
        if (!window.confirm(`Kirim kembali undangan untuk ${email}?`)) return;
        try {
            await workspaceAPI.resendInvitations(currentWorkspace.id, invitationId);
            toast.success('Undangan Berhasil dikirim kembali');
            fetchInvitations();
            setTimeoutDelay(60);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal mengirim undangan');
        }
    };

    const handleEditMember = (member) => {
        setEditMemberModal({ isOpen: true, member });
    };

    const handleInviteSuccess = () => {
        fetchMembers();
        fetchInvitations();
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'owner': return <Crown className="w-4 h-4 text-brand-warning" strokeWidth={1.5} />;
            case 'admin': return <Shield className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />;
            case 'project_manager': return <ClipboardList className="w-4 h-4 text-purple-500" strokeWidth={1.5} />;
            case 'member': return <User className="w-4 h-4 text-brand-text-secondary" strokeWidth={1.5} />;
            case 'sales': return <Briefcase className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />;
            case 'client': return <Briefcase className="w-4 h-4 text-blue-500" strokeWidth={1.5} />;
            case 'guest': return <Eye className="w-4 h-4 text-brand-text-muted" strokeWidth={1.5} />;
            default: return null;
        }
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'owner': return 'bg-brand-warning/10 text-brand-warning';
            case 'admin': return 'bg-brand-primary/10 text-brand-primary';
            case 'project_manager': return 'bg-purple-100 text-purple-600';
            case 'member': return 'bg-brand-accent-soft text-brand-text-secondary';
            case 'sales': return 'bg-emerald-100 text-emerald-700';
            case 'client': return 'bg-blue-100 text-blue-600';
            case 'guest': return 'bg-brand-background text-brand-text-muted';
            default: return '';
        }
    };

    if (!currentWorkspace) {
        return (
            <Layout title="Team">
                <div className="empty-state">
                    <p className="text-brand-text-muted">Please select a workspace first</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Team" subtitle="Manage workspace members and permissions">
            <div className="space-y-6" data-testid="team-page">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="text-sm text-brand-text-secondary">
                            {members.length} {members.length === 1 ? 'member' : 'members'} in this workspace
                        </p>
                    </div>
                    <Button 
                        onClick={() => setIsModalOpen(true)}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        data-testid="invite-member-btn"
                    >
                        <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Invite Member
                    </Button>
                </div>

                {/* Members List */}
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                    </div>
                ) : (
                    <Card className="border-brand-border shadow-card">
                        <div className="divide-y divide-brand-border">
                            {members.map((member) => (
                                <div 
                                    key={member.user_id}
                                    className="flex items-center gap-4 p-4 hover:bg-brand-accent-soft/30 transition-colors"
                                    data-testid={`member-${member.user_id}`}
                                >
                                    {/* Avatar */}
                                    <div className="avatar w-10 h-10 text-sm bg-brand-primary text-white">
                                        {getInitials(member.user_name)}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-brand-text-primary">
                                            {member.user_name}
                                        </p>
                                        <p className="text-sm text-brand-text-muted flex items-center gap-1">
                                            <Mail className="w-3 h-3" strokeWidth={1.5} />
                                            {member.user_email}
                                        </p>
                                        {/* Show space access if member has limited space access */}
                                        {member.space_ids && member.space_ids.length > 0 && (
                                            <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                                                <Briefcase className="w-3 h-3" strokeWidth={1.5} />
                                                Akses: {member.space_ids.length} space
                                            </p>
                                        )}
                                    </div>

                                    {/* Role Badge */}
                                    <div className={cn(
                                        'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium capitalize',
                                        getRoleBadgeClass(member.role)
                                    )}>
                                        {getRoleIcon(member.role)}
                                        {member.role}
                                    </div>

                                    {/* Actions */}
                                    {member.role !== 'owner' && (
                                        <div className="flex items-center gap-1">
                                            {/* Edit button for members with space restrictions or client/member/guest roles */}
                                            {/* member.role !== 'admin' && */}
                                            { true && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-brand-primary hover:bg-brand-primary/10"
                                                    onClick={() => handleEditMember(member)}
                                                    title="Edit akses space"
                                                >
                                                    <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-brand-error hover:bg-brand-error/10"
                                                onClick={() => handleRemoveMember(member.user_id, member.user_name)}
                                            >
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Pending Invitations */}
                {invitations.length > 0 && (
                    <Card className="border-brand-border shadow-card mt-4">
                        <CardHeader>
                            <CardTitle className="text-lg font-heading flex items-center gap-2">
                                <Clock className="w-5 h-5 text-brand-warning" strokeWidth={1.5} />
                                Undangan Tertunda ({invitations.length})
                            </CardTitle>
                        </CardHeader>
                        <div className="divide-y divide-brand-border">
                            {invitations.map((inv) => (
                                <div 
                                    key={inv.id}
                                    className="flex items-center gap-4 p-4 bg-brand-warning/5"
                                    data-testid={`invitation-${inv.id}`}
                                >
                                    {/* Avatar */}
                                    <div className="avatar w-10 h-10 text-sm bg-brand-warning/20 text-brand-warning border-2 border-dashed border-brand-warning">
                                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-brand-text-primary">
                                            {inv.email}
                                        </p>
                                        <p className="text-xs text-brand-text-muted">
                                            Invited by {inv.invited_by_name} • Expires {formatDate(inv.expires_at)}
                                        </p>
                                    </div>

                                    {/* Role Badge */}
                                    <div className={cn(
                                        'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium capitalize',
                                        getRoleBadgeClass(inv.role)
                                    )}>
                                        {getRoleIcon(inv.role)}
                                        {inv.role}
                                    </div>

                                    {/* Status */}
                                    <span className="text-xs px-2 py-1 bg-brand-warning/10 text-brand-warning rounded-full">
                                        Pending
                                    </span>

                                    {/* Cancel button */}
                                    <Button
                                        title="Kirim Ulang Undangan"
                                        variant="ghost"
                                        className="text-brand-primary hover:bg-primary-text-brand-primary/10 lg:ml-4 text-sm"
                                        onClick={() => handleResendInvitation(inv.id, inv.email)}
                                        disabled={timeout > 0}
                                    >
                                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                                        <span>Resend Invitation</span>
                                        { timeout > 0 && <span className="text-xs">({timeout})</span>}
                                    </Button>
                                    <Button
                                        title="Batalkan Undangan"
                                        variant="ghost"
                                        size="icon"
                                        className="text-brand-error hover:bg-brand-error/10"
                                        onClick={() => handleCancelInvitation(inv.id, inv.email)}
                                    >
                                        <X className="w-4 h-4" strokeWidth={1.5} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Role Descriptions */}
                <Card className="border-brand-border shadow-card">
                    <CardHeader>
                        <CardTitle className="text-lg font-heading">Hak Akses Role</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div className="p-4 rounded-lg bg-brand-warning/5 border border-brand-warning/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <Crown className="w-5 h-5 text-brand-warning" strokeWidth={1.5} />
                                    <span className="font-semibold">Owner</span>
                                </div>
                                <p className="text-sm text-brand-text-secondary">
                                    Akses penuh. Dapat menghapus workspace dan semua pengaturan.
                                </p>
                            </div>
                            <div className="p-4 rounded-lg bg-brand-primary/5 border border-brand-primary/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <Shield className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                                    <span className="font-semibold">Admin</span>
                                </div>
                                <p className="text-sm text-brand-text-secondary">
                                    Kelola member, space, dan pengaturan. Tidak bisa hapus workspace.
                                </p>
                            </div>
                            <div className="p-4 rounded-lg bg-brand-accent-soft border border-brand-border">
                                <div className="flex items-center gap-2 mb-2">
                                    <User className="w-5 h-5 text-brand-text-secondary" strokeWidth={1.5} />
                                    <span className="font-semibold">Member</span>
                                </div>
                                <p className="text-sm text-brand-text-secondary">
                                    Buat dan kelola task, space, dan time entries.
                                </p>
                            </div>
                            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <Briefcase className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                                    <span className="font-semibold">Client</span>
                                </div>
                                <p className="text-sm text-brand-text-secondary">
                                    Akses baca-saja ke space tertentu. Cocok untuk klien eksternal.
                                </p>
                            </div>
                            <div className="p-4 rounded-lg bg-brand-background border border-brand-border">
                                <div className="flex items-center gap-2 mb-2">
                                    <Eye className="w-5 h-5 text-brand-text-muted" strokeWidth={1.5} />
                                    <span className="font-semibold">Guest</span>
                                </div>
                                <p className="text-sm text-brand-text-secondary">
                                    Akses baca-saja. Dapat komentar pada task yang ditugaskan.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Invite Modal */}
            <InviteMemberModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                workspaceId={currentWorkspace?.id}
                onSuccess={handleInviteSuccess}
            />

            {/* Edit Client Member Modal */}
            <EditClientMemberModal
                isOpen={editMemberModal.isOpen}
                onClose={() => setEditMemberModal({ isOpen: false, member: null })}
                workspaceId={currentWorkspace?.id}
                member={editMemberModal.member}
                onSuccess={handleInviteSuccess}
            />
        </Layout>
    );
}

// Invite Member Modal
const InviteMemberModal = ({ isOpen, onClose, workspaceId, onSuccess }) => {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('member');
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSpaces, setLoadingSpaces] = useState(false);

    // Fetch spaces when modal opens
    useEffect(() => {
        if (isOpen && workspaceId) {
            fetchSpaces();
        }
    }, [isOpen, workspaceId]);

    // Reset selected spaces when role changes to admin (admin gets all access)
    useEffect(() => {
        if (role === 'admin') {
            setSelectedSpaces([]);
        }
    }, [role]);

    const fetchSpaces = async () => {
        setLoadingSpaces(true);
        try {
            const response = await spaceAPI.getByWorkspace(workspaceId);
            setSpaces(response.data);
        } catch (error) {
            console.error('Failed to fetch spaces:', error);
        } finally {
            setLoadingSpaces(false);
        }
    };

    const handleRoleChange = (newRole) => {
        setRole(newRole);
        // Admin gets full access, reset selection
        if (newRole === 'admin') {
            setSelectedSpaces([]);
        }
    };

    const handleSpaceToggle = (spaceId) => {
        setSelectedSpaces(prev => 
            prev.includes(spaceId)
                ? prev.filter(id => id !== spaceId)
                : [...prev, spaceId]
        );
    };

    const handleInvite = async () => {
        if (!email.trim()) {
            toast.error('Masukkan alamat email');
            return;
        }

        // Client must have at least one space selected
        if (role === 'client' && selectedSpaces.length === 0) {
            toast.error('Pilih minimal satu space untuk client');
            return;
        }

        setLoading(true);
        try {
            // Admin gets null (full access), others get selected spaces or null if none selected
            let spaceIdsToSend;

            if (role === 'admin') {
                spaceIdsToSend = null;
            } else if (['guest', 'member', 'sales'].includes(role)) {
                spaceIdsToSend = selectedSpaces.length === 0
                ? spaces.map(space => space.id)
                : selectedSpaces;
            } else {
                spaceIdsToSend = selectedSpaces;
            }
                        
            
            const response = await workspaceAPI.addMember(workspaceId, {
                user_email: email,
                role: role,
                space_ids: spaceIdsToSend,
            });
            
            // Check if it was an invitation or direct add
            if (response.data.status === 'pending') {
                toast.success(`Undangan terkirim ke ${email}`);
            } else {
                toast.success('Member berhasil ditambahkan');
            }
            
            setEmail('');
            setRole('member');
            setSelectedSpaces([]);
            onClose();
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal menambahkan member');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="invite-modal">
                <DialogHeader>
                    <DialogTitle className="font-heading">Undang Anggota Tim</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    <div>
                        <Label>Alamat Email</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="colleague@company.com"
                            className="mt-1"
                            data-testid="invite-email-input"
                        />
                        <p className="text-xs text-brand-text-muted mt-1">
                            Jika user belum terdaftar, undangan akan dikirim via email.
                        </p>
                    </div>

                    <div>
                        <Label>Role</Label>
                        <Select value={role} onValueChange={handleRoleChange}>
                            <SelectTrigger className="mt-1" data-testid="invite-role-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="project_manager">Project Manager</SelectItem>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="sales">Sales</SelectItem>
                                <SelectItem value="client">Client (Read-only)</SelectItem>
                                <SelectItem value="guest">Guest</SelectItem>
                            </SelectContent>
                        </Select>
                        {role === 'admin' && (
                            <p className="text-xs text-brand-text-muted mt-1">
                                Admin memiliki akses penuh ke semua space
                            </p>
                        )}
                        {role === 'project_manager' && (
                            <p className="text-xs text-brand-text-muted mt-1">
                                Project Manager dapat akses menu Daily Standup, tanpa popup Daily Standup
                            </p>
                        )}
                        {role === 'client' && (
                            <p className="text-xs text-brand-text-muted mt-1">
                                Client hanya bisa melihat space yang dipilih (read-only)
                            </p>
                        )}
                        {role === 'member' && (
                            <p className="text-xs text-brand-text-muted mt-1">
                                Jika tidak memilih space, member dapat akses semua space
                            </p>
                        )}
                        {role === 'sales' && (
                            <p className="text-xs text-brand-text-muted mt-1">
                                Sales memiliki akses ke CRM Area (Contact, Lead, Client, Project)
                            </p>
                        )}
                        {role === 'guest' && (
                            <p className="text-xs text-brand-text-muted mt-1">
                                Guest memiliki akses terbatas ke space yang dipilih
                            </p>
                        )}
                    </div>

                    {/* Space Selection for all roles except admin */}
                    {role !== 'admin' && (
                        <div>
                            <Label className="flex items-center gap-2">
                                <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                                {role === 'client' ? 'Pilih Space untuk Client' : 'Batasi Akses ke Space (Opsional)'}
                            </Label>
                            {role !== 'client' && (
                                <p className="text-xs text-brand-text-muted mt-1">
                                    Kosongkan jika ingin akses ke semua space
                                </p>
                            )}
                            {loadingSpaces ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                                </div>
                            ) : spaces.length === 0 ? (
                                <p className="text-sm text-brand-text-muted py-2">
                                    Belum ada space di workspace ini
                                </p>
                            ) : (
                                <div className="mt-2 max-h-48 overflow-y-auto border border-brand-border rounded-lg">
                                    {spaces.map((space) => (
                                        <label
                                            key={space.id}
                                            className="flex items-center gap-3 p-3 hover:bg-brand-accent-soft cursor-pointer border-b border-brand-border last:border-b-0"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedSpaces.includes(space.id)}
                                                onChange={() => handleSpaceToggle(space.id)}
                                                className="w-4 h-4 text-brand-primary rounded border-brand-border focus:ring-brand-primary"
                                            />
                                            <div
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: space.color || '#2F6F63' }}
                                            />
                                            <span className="text-sm text-brand-text-primary">{space.name}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                            {selectedSpaces.length > 0 && (
                                <p className="text-xs text-brand-primary mt-2">
                                    {selectedSpaces.length} space dipilih
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={onClose}>Batal</Button>
                    <Button 
                        onClick={handleInvite}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        disabled={loading || (role === 'client' && selectedSpaces.length === 0)}
                        data-testid="invite-submit-btn"
                    >
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Tambah Member
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};


// Edit Member Modal - For updating space access (all roles except admin)
const EditClientMemberModal = ({ isOpen, onClose, workspaceId, member, onSuccess }) => {
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSpaces, setLoadingSpaces] = useState(false);

    console.log('Member:', member);
    const isClient = member?.role === 'client';
    const [email, setEmail] = useState();
    const [role, setRole] = useState();

    // Fetch spaces and set initial selection when modal opens
    useEffect(() => {
        if (isOpen && workspaceId && member) {
            fetchSpaces();
            setEmail(member.user_email);
            setRole(member.role);
            setSelectedSpaces(member.space_ids || []);
        }
    }, [isOpen, workspaceId, member]);

    const fetchSpaces = async () => {
        setLoadingSpaces(true);
        try {
            const response = await spaceAPI.getByWorkspace(workspaceId);
            setSpaces(response.data);
        } catch (error) {
            console.error('Failed to fetch spaces:', error);
        } finally {
            setLoadingSpaces(false);
        }
    };

    const handleSpaceToggle = (spaceId) => {
        setSelectedSpaces(prev => 
            prev.includes(spaceId)
                ? prev.filter(id => id !== spaceId)
                : [...prev, spaceId]
        );
    };

    // Fetch spaces when modal opens
    useEffect(() => {
        if (isOpen && workspaceId) {
            fetchSpaces();
        }
    }, [isOpen, workspaceId]);

    // Reset selected spaces when role changes to admin (admin gets all access)
    useEffect(() => {
        if (role === 'admin') {
            setSelectedSpaces([]);
        }
    }, [role]);


    const handleRoleChange = (newRole) => {
        setRole(newRole);
        // Admin gets full access, reset selection
        if (newRole === 'admin') {
            setSelectedSpaces([]);
        }
    };

    const handleSave = async () => {
        if (!email.trim()) {
            toast.error('Masukkan alamat email');
            return;
        }

        // Client must have at least one space
        if (role === 'client' && selectedSpaces.length === 0) {
            toast.error('Pilih minimal satu space untuk client');
            return;
        }


        setLoading(true);
        try {
            await workspaceAPI.updateMember(workspaceId, member.user_id, {
                space_ids: role === 'admin' ? null : selectedSpaces,
                role,
                user_email: email
            });
            toast.success('Akses member berhasil diperbarui');
            setEmail('');
            setRole('member');
            setSelectedSpaces([]);
            onClose();
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal memperbarui akses');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="invite-modal">
                <DialogHeader>
                    <DialogTitle className="font-heading">Ubah Anggota Tim</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    <div>
                        <Label>Alamat Email</Label>
                        <Input
                            disabled
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="colleague@company.com"
                            className="mt-1"
                            data-testid="invite-email-input"
                        />
                    </div>

                    <div>
                        <Label>Role</Label>
                        <Select value={role} onValueChange={handleRoleChange}>
                            <SelectTrigger className="mt-1" data-testid="invite-role-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="project_manager">Project Manager</SelectItem>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="client">Client (Read-only)</SelectItem>
                                <SelectItem value="guest">Guest</SelectItem>
                            </SelectContent>
                        </Select>
                        {role === 'admin' && (
                            <p className="text-xs text-brand-text-muted my-1">
                                Admin memiliki akses penuh ke semua space
                            </p>
                        )}
                        {role === 'project_manager' && (
                            <p className="text-xs text-brand-text-muted my-1">
                                Project Manager dapat akses menu Daily Standup, tanpa popup Daily Standup
                            </p>
                        )}
                        {role === 'client' && (
                            <p className="text-xs text-brand-text-muted my-1">
                                Client hanya bisa melihat space yang dipilih (read-only)
                            </p>
                        )}
                        {role === 'member' && (
                            <p className="text-xs text-brand-text-muted my-1">
                                Jika tidak memilih space, member dapat akses semua space
                            </p>
                        )}
                        {role === 'guest' && (
                            <p className="text-xs text-brand-text-muted my-1">
                                Guest memiliki akses terbatas ke space yang dipilih
                            </p>
                        )}
                    </div>

                    {/* Space Selection for all roles except admin */}
                    {role !== 'admin' && (
                        <div>
                            <Label className="flex items-center gap-2">
                                <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                                {role === 'client' ? 'Pilih Space untuk Client' : 'Batasi Akses ke Space (Opsional)'}
                            </Label>
                            {role !== 'client' && (
                                <p className="text-xs text-brand-text-muted mt-1">
                                    Kosongkan jika ingin akses ke semua space
                                </p>
                            )}
                            {loadingSpaces ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                                </div>
                            ) : spaces.length === 0 ? (
                                <p className="text-sm text-brand-text-muted py-2">
                                    Belum ada space di workspace ini
                                </p>
                            ) : (
                                <div className="mt-2 max-h-48 overflow-y-auto border border-brand-border rounded-lg">
                                    {spaces.map((space) => (
                                        <label
                                            key={space.id}
                                            className="flex items-center gap-3 p-3 hover:bg-brand-accent-soft cursor-pointer border-b border-brand-border last:border-b-0"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedSpaces.includes(space.id)}
                                                onChange={() => handleSpaceToggle(space.id)}
                                                className="w-4 h-4 text-brand-primary rounded border-brand-border focus:ring-brand-primary"
                                            />
                                            <div
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: space.color || '#2F6F63' }}
                                            />
                                            <span className="text-sm text-brand-text-primary">{space.name}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                            {selectedSpaces.length > 0 && (
                                <p className="text-xs text-brand-primary mt-2">
                                    {selectedSpaces.length} space dipilih
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={onClose}>Batal</Button>
                    <Button 
                        onClick={handleSave}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        disabled={loading || (role === 'client' && selectedSpaces.length === 0)}
                        data-testid="invite-submit-btn"
                    >
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Simpan Perubahan
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
